import { and, desc, eq, gte, isNull, or } from "drizzle-orm";
import {
  adminNotificationReads,
  adminNotifications,
} from "@/server/platform/db/schema/notifications";
import type { PermissionId } from "@/modules/roles/domain/contracts/permissions";
import {
  NOTIFICATION_MAX_DROPDOWN_ITEMS,
  NOTIFICATION_RETENTION_DAYS,
} from "../config";
import { resolveEffectiveSeverity } from "../resolve-effective-severity";
import { canUserSeeNotification } from "./notification-access";
import type { NotificationSeverity } from "./notification-summary";
import type { DbTransaction } from "@/shared/types";
import type { OffsetPageQueryDTO, OffsetPageDTO } from "@/shared/contracts/base";

export type AdminNotificationRow = {
  id: string;
  type: string;
  title: string;
  body: string | null;
  href: string;
  severity: NotificationSeverity;
  createdAt: string;
  readAt: string | null;
  isUnread: boolean;
};

function retentionCutoff(): Date {
  const d = new Date();
  d.setDate(d.getDate() - NOTIFICATION_RETENTION_DAYS);
  return d;
}

function mapRow(row: {
  id: string;
  type: string;
  title: string;
  body: string | null;
  href: string;
  severity: NotificationSeverity;
  createdAt: Date;
  readAt: Date | null;
}): AdminNotificationRow {
  const createdAt = row.createdAt.toISOString();
  return {
    id: row.id,
    type: row.type,
    title: row.title,
    body: row.body,
    href: row.href,
    severity: resolveEffectiveSeverity(row.type, row.severity, createdAt),
    createdAt,
    readAt: row.readAt?.toISOString() ?? null,
    isUnread: row.readAt == null,
  };
}

function isVisible(
  permissions: PermissionId[],
  row: {
    audience: "permission" | "all_admins";
    requiredPermission: string | null;
  },
): boolean {
  return canUserSeeNotification(permissions, row);
}

export async function listAdminNotifications(
  userId: string,
  permissions: PermissionId[],
  query: OffsetPageQueryDTO & { unreadOnly?: boolean; type?: string },
  db: DbTransaction,
): Promise<OffsetPageDTO<AdminNotificationRow>> {
  const cutoff = retentionCutoff();
  const now = new Date();

  const rows = await db
    .select({
      id: adminNotifications.id,
      type: adminNotifications.type,
      title: adminNotifications.title,
      body: adminNotifications.body,
      href: adminNotifications.href,
      severity: adminNotifications.severity,
      audience: adminNotifications.audience,
      requiredPermission: adminNotifications.requiredPermission,
      createdAt: adminNotifications.createdAt,
      readAt: adminNotificationReads.readAt,
    })
    .from(adminNotifications)
    .leftJoin(
      adminNotificationReads,
      and(
        eq(adminNotificationReads.notificationId, adminNotifications.id),
        eq(adminNotificationReads.userId, userId),
      ),
    )
    .where(
      and(
        gte(adminNotifications.createdAt, cutoff),
        or(
          isNull(adminNotifications.expiresAt),
          gte(adminNotifications.expiresAt, now),
        ),
        query.unreadOnly ? isNull(adminNotificationReads.readAt) : undefined,
      ),
    )
    .orderBy(desc(adminNotifications.createdAt))
    .limit(query.limit + query.offset + 200);

  let visible = rows.filter((row) => isVisible(permissions, row));
  if (query.type) {
    visible = visible.filter((row) => row.type === query.type);
  }
  const total = query.unreadOnly
    ? visible.filter((row) => row.readAt == null).length
    : visible.length;
  const page = visible.slice(query.offset, query.offset + query.limit);

  return {
    data: page.map(mapRow),
    meta: { total, limit: query.limit, offset: query.offset },
  };
}

export async function getUnreadNotificationCount(
  userId: string,
  permissions: PermissionId[],
  db: DbTransaction,
): Promise<number> {
  const cutoff = retentionCutoff();
  const now = new Date();

  const rows = await db
    .select({
      audience: adminNotifications.audience,
      requiredPermission: adminNotifications.requiredPermission,
      readAt: adminNotificationReads.readAt,
    })
    .from(adminNotifications)
    .leftJoin(
      adminNotificationReads,
      and(
        eq(adminNotificationReads.notificationId, adminNotifications.id),
        eq(adminNotificationReads.userId, userId),
      ),
    )
    .where(
      and(
        gte(adminNotifications.createdAt, cutoff),
        or(
          isNull(adminNotifications.expiresAt),
          gte(adminNotifications.expiresAt, now),
        ),
        isNull(adminNotificationReads.readAt),
      ),
    );

  return rows.filter((row) => isVisible(permissions, row)).length;
}

export async function getNotificationDropdownRows(
  userId: string,
  permissions: PermissionId[],
  db: DbTransaction,
): Promise<AdminNotificationRow[]> {
  const cutoff = retentionCutoff();
  const now = new Date();

  const rows = await db
    .select({
      id: adminNotifications.id,
      type: adminNotifications.type,
      title: adminNotifications.title,
      body: adminNotifications.body,
      href: adminNotifications.href,
      severity: adminNotifications.severity,
      audience: adminNotifications.audience,
      requiredPermission: adminNotifications.requiredPermission,
      createdAt: adminNotifications.createdAt,
      readAt: adminNotificationReads.readAt,
    })
    .from(adminNotifications)
    .leftJoin(
      adminNotificationReads,
      and(
        eq(adminNotificationReads.notificationId, adminNotifications.id),
        eq(adminNotificationReads.userId, userId),
      ),
    )
    .where(
      and(
        gte(adminNotifications.createdAt, cutoff),
        or(
          isNull(adminNotifications.expiresAt),
          gte(adminNotifications.expiresAt, now),
        ),
      ),
    )
    .orderBy(desc(adminNotifications.createdAt))
    .limit(100);

  const visible = rows
    .filter((row) => isVisible(permissions, row))
    .map(mapRow);

  const unread = visible.filter((row) => row.isUnread);
  const read = visible.filter((row) => !row.isUnread);
  unread.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  read.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return [...unread, ...read].slice(0, NOTIFICATION_MAX_DROPDOWN_ITEMS);
}

export async function countUnreadBySectionType(
  userId: string,
  permissions: PermissionId[],
  db: DbTransaction,
): Promise<Map<string, number>> {
  const cutoff = retentionCutoff();
  const now = new Date();

  const rows = await db
    .select({
      type: adminNotifications.type,
      audience: adminNotifications.audience,
      requiredPermission: adminNotifications.requiredPermission,
      readAt: adminNotificationReads.readAt,
    })
    .from(adminNotifications)
    .leftJoin(
      adminNotificationReads,
      and(
        eq(adminNotificationReads.notificationId, adminNotifications.id),
        eq(adminNotificationReads.userId, userId),
      ),
    )
    .where(
      and(
        gte(adminNotifications.createdAt, cutoff),
        or(
          isNull(adminNotifications.expiresAt),
          gte(adminNotifications.expiresAt, now),
        ),
        isNull(adminNotificationReads.readAt),
      ),
    );

  const counts = new Map<string, number>();

  for (const row of rows) {
    if (!isVisible(permissions, row)) continue;
    const sectionKey = sectionKeyForType(row.type);
    counts.set(sectionKey, (counts.get(sectionKey) ?? 0) + 1);
  }

  return counts;
}

export function sectionKeyForType(type: string): string {
  if (type.startsWith("wallet.topup")) return "topup";
  if (type.startsWith("wallet.kyc")) return "kyc";
  if (type.startsWith("compliance.suspicious")) return "suspicious";
  return "other";
}

export async function getAdminNotificationById(
  notificationId: string,
  userId: string,
  permissions: PermissionId[],
  db: DbTransaction,
): Promise<AdminNotificationRow | null> {
  const [row] = await db
    .select({
      id: adminNotifications.id,
      type: adminNotifications.type,
      title: adminNotifications.title,
      body: adminNotifications.body,
      href: adminNotifications.href,
      severity: adminNotifications.severity,
      audience: adminNotifications.audience,
      requiredPermission: adminNotifications.requiredPermission,
      createdAt: adminNotifications.createdAt,
      readAt: adminNotificationReads.readAt,
    })
    .from(adminNotifications)
    .leftJoin(
      adminNotificationReads,
      and(
        eq(adminNotificationReads.notificationId, adminNotifications.id),
        eq(adminNotificationReads.userId, userId),
      ),
    )
    .where(eq(adminNotifications.id, notificationId))
    .limit(1);

  if (!row || !isVisible(permissions, row)) return null;
  return mapRow(row);
}
